import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';

const AddressAutocomplete = forwardRef(
  (
    {
      inputRef,
      disabled,
      locationFlow,
      onInputChange,
      onSelectDetails,
      defaultValue = '',
      inputVariant = 'standard',
      label = 'Address',
      validattionErrors,
      errorMsg,
      inputType
    },
    ref
  ) => {
    const [options, setOptions] = useState([]);
    const [inputValueApi, setInputValueApi] = useState(defaultValue);
    const [inputValue, setInputValue] = useState(defaultValue);
    const [highlightedOption, setHighlightedOption] = useState(null);
    const [addressError, setAddressError] = useState('');
    const autocompleteService = useRef(null);

    useEffect(() => {
      setInputValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
      if (!autocompleteService.current && window.google) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
      }
    }, []);

    useEffect(() => {
      if (!autocompleteService.current || !inputValue) {
        setOptions([]);
        return;
      }

      const cleanedInput = inputValue.replace(/[^a-zA-Z0-9\s,]/g, '');

      if (cleanedInput) {
        autocompleteService.current.getPlacePredictions(
          {
            input: cleanedInput,
            componentRestrictions: { country: ['us', 'ca', 'mx'] }
          },
          predictions => {
            const sanitized = (predictions || []).map(p => ({
              ...p,
              description: inputValue.includes(',')
                ? p.description
                : p.description.replace(/,/g, '')
            }));
            setOptions(sanitized);
          }
        );
      } else {
        setOptions([]);
      }
    }, [inputValue]);

    const handleSelect = option => {
      if (!option?.place_id) return;

      if (
        inputType === 'quickQuote' &&
        window.google?.maps?.places?.PlacesService
      ) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.getDetails(
          {
            placeId: option.place_id,
            fields: ['name', 'address_components', 'formatted_address']
          },
          (place, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              const components = place.address_components || [];

              const extract = (type, useShort = false) =>
                components.find(c => c.types.includes(type))?.[
                  useShort ? 'short_name' : 'long_name'
                ] || '';

              const streetNumber = extract('street_number');
              const route = extract('route');
              const streetLines =
                streetNumber || route ? `${streetNumber} ${route}`.trim() : '';

              const hasStreetLine = Boolean(streetLines);

              let countryCode = extract('country', true);
              if (countryCode === 'US') countryCode = 'USA';

              const addressDetails = {
                city: extract('locality') || extract('sublocality') || '',
                state: extract('administrative_area_level_1', true),
                zip: extract('postal_code'),
                country: countryCode,
                streetLines,
                hasStreetLine,
                residential: false
              };

              if (place.name) {
                const isProbablyCompany = /^[A-Za-z\s&.,'-]+$/.test(
                  place.name.trim()
                );
                if (isProbablyCompany) {
                  addressDetails.company = place.name.trim();
                } else {
                  addressDetails.company = '';
                }
              }

              const fullAddress =
                place.formatted_address || option.description || '';
              const streetOnly = fullAddress.split(',')[0];
              const finalValue =
                locationFlow === 'LTL' && hasStreetLine
                  ? streetOnly
                  : fullAddress;

              const displayValue = place.name
                ? `${place.name}, ${fullAddress}`
                : fullAddress;

              setInputValue(displayValue);
              setInputValueApi(finalValue);
              setAddressError('');

              onSelectDetails?.(addressDetails);
            }
          }
        );

        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: option.place_id }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const components = results[0].address_components;

          const extract = (type, useShort = false) =>
            components.find(c => c.types.includes(type))?.[
              useShort ? 'short_name' : 'long_name'
            ] || '';

          const streetNumber = extract('street_number');
          const route = extract('route');
          const streetLines =
            streetNumber || route ? `${streetNumber} ${route}`.trim() : '';

          const hasStreetLine = Boolean(streetLines);

          let countryCode = extract('country', true);
          if (countryCode === 'US') countryCode = 'USA';

          const addressDetails = {
            city: extract('locality') || extract('sublocality') || '',
            state: extract('administrative_area_level_1', true),
            zip: extract('postal_code'),
            country: countryCode,
            streetLines,
            hasStreetLine,
            residential: false
          };

          const fullAddress = results[0].formatted_address;
          const streetOnly = fullAddress.split(',')[0];
          const finalValue =
            locationFlow === 'LTL' && hasStreetLine ? streetOnly : fullAddress;

          setInputValue(fullAddress);
          setInputValueApi(finalValue);
          setAddressError('');

          onSelectDetails?.(addressDetails);
          setHighlightedOption(null);
        }
      });
    };

    const handleBlur = () => {
      setHighlightedOption(null);
      setAddressError('');
    };

    return (
      <Autocomplete
        freeSolo
        disabled={disabled}
        clearOnEscape
        disableClearable={false}
        size='small'
        sx={{ width: '100%', backgroundColor: 'white', mt: 1 }}
        options={options}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option?.description || ''
        }
        filterOptions={x => x}
        inputValue={inputValue}
        value={
          options.find(opt =>
            typeof opt === 'string'
              ? opt === inputValue
              : opt.description === inputValue
          ) || null
        }
        onHighlightChange={(event, option) => setHighlightedOption(option)}
        onBlur={handleBlur}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            setInputValue(newInputValue);
            setAddressError('');

            const finalValue =
              locationFlow === 'LTL'
                ? newInputValue.split(',')[0]
                : newInputValue;

            onInputChange?.(finalValue);
          }
        }}
        onChange={(event, newValue) => {
          if (newValue) {
            const fullAddress = newValue?.description || '';
            const mainText = newValue?.structured_formatting?.main_text || '';
            const descFirst = fullAddress.split(',')[0] || '';
            const termFirst = newValue?.terms?.[0]?.value || '';

            const streetOnly = mainText || descFirst || termFirst || '';
            const finalValue =
              locationFlow === 'LTL' ? streetOnly : fullAddress;

            const companyCandidate =
              inputType === 'quickQuote'
                ? mainText || termFirst || descFirst
                : null;

            if (inputType === 'quickQuote' && companyCandidate) {
              setInputValue(`${companyCandidate}, ${fullAddress}`);
            } else {
              setInputValue(fullAddress);
            }

            setInputValueApi(finalValue);
            onInputChange?.(finalValue);

            handleSelect(newValue);
            setAddressError('');
          } else {
            setInputValue('');
            setInputValueApi('');
            onInputChange?.('');
            onSelectDetails?.(null);
            setAddressError('');
          }
        }}
        renderInput={params => (
          <TextField
            inputRef={inputRef}
            placeholder={'Search Address with Google API'}
            {...params}
            error={
              Boolean(validattionErrors?.[errorMsg]) || Boolean(addressError)
            }
            helperText={addressError || validattionErrors?.[errorMsg]}
            size='small'
            fullWidth
            variant={'standard'}
            label={label}
            inputProps={{
              ...params.inputProps,
              style: { fontSize: 13 }
            }}
          />
        )}
      />
    );
  }
);

export default AddressAutocomplete;
